/**
 * Dates cross the consultancy API as UTC ISO-8601 strings. A null zone renders
 * deterministically in UTC until hydration identifies the visitor's zone.
 */
const toUtcDate = (value: string | null | undefined): Date | null => {
  if (!value || !/(?:Z|\+00:00)$/.test(value)) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const getVisitorTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const formatConsultancyDate = (
  value: string | null | undefined,
  locale: string,
  timeZone: string | null
): string => {
  const date = toUtcDate(value);

  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: timeZone ?? 'UTC',
  }).format(date);
};

export const formatConsultancyDateTime = (
  value: string | null | undefined,
  locale: string,
  timeZone: string | null
): string => {
  const date = toUtcDate(value);

  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: 'short',
    timeZone: timeZone ?? 'UTC',
    year: 'numeric',
  }).format(date);
};

export const utcIsoToLocalDateTimeInput = (
  value: string | null | undefined
): string => {
  const date = toUtcDate(value);

  if (!date) {
    return '';
  }

  const pad = (part: number): string => part.toString().padStart(2, '0');

  return `${date.getFullYear().toString()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Converts a datetime-local wall-clock value to UTC, rejecting invalid DST gaps. */
export const localDateTimeToUtcIso = (value: string): string | null => {
  const match =
    /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2})$/.exec(
      value
    );

  if (!match?.groups) {
    return null;
  }

  const year = Number(match.groups.year);
  const month = Number(match.groups.month);
  const day = Number(match.groups.day);
  const hour = Number(match.groups.hour);
  const minute = Number(match.groups.minute);
  const date = new Date(year, month - 1, day, hour, minute);

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    hour > 23 ||
    minute > 59 ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  ) {
    return null;
  }

  return date.toISOString();
};
